'use client';

import {
  User,
  LogOut,
  Settings,
  Crown,
  Shield,
  Users,
  Moon,
  Sun,
  ShieldCheck,
  Building2,
  Gamepad2,
  Crosshair,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSession } from '../../lib/AuthContext';
import { useOrganization } from '../../lib/OrganizationContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useSuperAdmin } from '../../hooks/useSuperAdmin';
import { useTheme } from '../theme-provider';
import { useNavigate } from 'react-router';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { OrganizationSelector } from '../OrganizationSelector';
import { SidebarTrigger } from '@/components/ui/sidebar';

const ROLE_DISPLAY_MAP: Record<string, { icon: typeof User; color: string; label: string }> = {
  owner: {
    icon: Crown,
    color: 'bg-primary/20 text-primary border-primary/30',
    label: 'Propietario',
  },
  admin: {
    icon: Shield,
    color: 'bg-primary/15 text-primary border-primary/25',
    label: 'Administrador',
  },
  member: {
    icon: Users,
    color: 'bg-muted text-muted-foreground border-border',
    label: 'Miembro',
  },
  coach: {
    icon: Gamepad2,
    color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
    label: 'Coach',
  },
  player: {
    icon: Crosshair,
    color: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/25',
    label: 'Jugador',
  },
};

const DEFAULT_ROLE_DISPLAY = {
  icon: User,
  color: 'bg-primary/20 text-primary border-primary/30',
  label: 'Usuario',
};

function getRoleDisplay(role: string | null) {
  return ROLE_DISPLAY_MAP[role ?? ''] ?? DEFAULT_ROLE_DISPLAY;
}

export const Navbar = () => {
  const { session, signOut } = useSession();
  const { currentOrganization } = useOrganization();
  const { getCurrentUserRole, isOwner, isAdmin } = usePermissions();
  const { isSuperAdmin } = useSuperAdmin();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const user = session?.user;
  const userRole = getCurrentUserRole();
  const [isDark, setIsDark] = useState(false);

  // Determinar si el tema actual es oscuro
  useEffect(() => {
    const updateIsDark = () => {
      if (theme === 'dark') {
        setIsDark(true);
      } else if (theme === 'light') {
        setIsDark(false);
      } else {
        // Tema "system"
        setIsDark(window.matchMedia('(prefers-color-scheme: dark)').matches);
      }
    };

    updateIsDark();

    // Escuchar cambios en el tema del sistema si el tema es "system"
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => updateIsDark();
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  // Función para alternar el tema
  const toggleTheme = () => {
    if (isDark) {
      setTheme('light');
    } else {
      setTheme('dark');
    }
  };

  const roleDisplay = getRoleDisplay(userRole);

  return (
    <header className="flex h-16 shrink-0 items-center gap-2 border-b border-border bg-card px-4 shadow-xs">
      <SidebarTrigger className="-ml-1" />

      <div className="flex items-center gap-2 ml-2">
        <h1 className="font-display text-lg font-semibold tracking-wide text-foreground">
          CoreUtils
        </h1>
        {currentOrganization && (
          <span className="text-sm text-muted-foreground">• {currentOrganization.name}</span>
        )}
      </div>

      <div className="ml-auto flex items-center gap-4">
        {/* Mostrar rol del usuario en mobile */}
        {user && currentOrganization && userRole && (
          <div className="hidden sm:block">
            <Badge
              className={`${roleDisplay.color} flex items-center gap-1.5 px-2 py-1 text-xs font-medium`}
            >
              <roleDisplay.icon className="w-3 h-3" />
              {roleDisplay.label}
            </Badge>
          </div>
        )}

        {/* Selector de organización */}
        {user && currentOrganization && (
          <div className="hidden md:block">
            <OrganizationSelector />
          </div>
        )}

        {user && (
          <>
            {/* Información del usuario en desktop */}
            <div className="hidden lg:block text-sm">
              <p className="font-medium text-foreground">{user.name || user.email}</p>
              <p className="text-xs text-muted-foreground">{user.email}</p>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-9 w-9 rounded-md overflow-hidden border border-border bg-muted p-0.5 hover:border-foreground/30 transition-colors"
                >
                  {user.avatar_url ? (
                    <img 
                      src={user.avatar_url} 
                      alt="Profile" 
                      width={32}
                      height={32}
                      loading="lazy"
                      className="h-full w-full object-cover rounded-lg" 
                    />
                  ) : (
                    <User className="h-5 w-5 text-primary" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {/* Información del usuario en mobile */}
                <div className="lg:hidden px-2 py-1.5 text-xs text-muted-foreground border-b mb-1">
                  <p className="font-medium text-foreground">{user.name || user.email}</p>
                  <p className="text-xs text-muted-foreground/60">{user.email}</p>
                </div>

                {/* Información del rol en el dropdown */}
                {userRole && (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground border-b mb-1">
                    <div className="flex items-center gap-2">
                      <roleDisplay.icon className="w-3 h-3" />
                      <span>
                        {roleDisplay.label} en {currentOrganization?.name}
                      </span>
                    </div>
                  </div>
                )}

                {/* Selector de organización en mobile */}
                {currentOrganization && (
                  <div className="md:hidden px-2 py-1.5 border-b mb-1">
                    <OrganizationSelector />
                  </div>
                )}

                {isSuperAdmin && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/super-admin')}>
                      <ShieldCheck className="mr-2 h-4 w-4" />
                      <span>Panel Super Admin</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}

                {(isOwner() || isAdmin()) && (
                  <DropdownMenuItem
                    onClick={() => navigate('/control/organization?tab=general')}
                  >
                    <Building2 className="mr-2 h-4 w-4" />
                    <span>Organización</span>
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Configuración</span>
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                  onSelect={(e) => e.preventDefault()}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    {isDark ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                    <span>{isDark ? 'Modo oscuro' : 'Modo claro'}</span>
                  </div>
                  <Switch
                    checked={isDark}
                    onCheckedChange={toggleTheme}
                    onClick={(e) => e.stopPropagation()}
                  />
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                  onClick={() => signOut()}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Cerrar sesión</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>
    </header>
  );
};
